import { useEffect, useRef, useState } from "react";
import { QuerySpec } from "@src/types/dashboards";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";
import { DURATION_GAUGE_WIDGET_MS, resolvePollingMs } from "../constants";

/** Last value of one entry's node. `value` is null whenever the row has
 *  nothing to show — no data, a failed fetch, or a value the API returned
 *  in a shape we don't render. Strings are accepted verbatim: an entry may
 *  be bound to a `data_type = "string"` node. */
export interface MultipleTextValuesEntryValue {
  value: number | string | null;
  /** True when the row has nothing to draw: empty response or failed
   *  fetch. Drives the shared "no data" panel when EVERY row is like
   *  this; otherwise the row just renders an em-dash. */
  hasNoData: boolean;
}

export interface UseMultipleTextValuesResult {
  /** One entry per binding, in binding order. */
  entries: MultipleTextValuesEntryValue[];
  /** True only while the first round of responses is outstanding, so the
   *  widget shows one spinner instead of one per row. */
  isLoading: boolean;
  /** True when every row came back empty. */
  hasNoData: boolean;
}

interface QueryValue {
  time: string;
  value: number | string | null;
}

const NO_ENTRY: MultipleTextValuesEntryValue = { value: null, hasNoData: true };

/**
 * Fetches the last value of EVERY binding of a `multiple-text-values`
 * widget — one request per binding, each against that binding's own
 * machine (`binding.source`) and node (`binding.address`), all in flight
 * together.
 *
 * This is deliberately its own hook rather than a widening of
 * `useLastWidgetValue`, which reads `bindings[0]` on `query.source` and is
 * shared with the gauge. Multi-line solved the same problem the same way
 * (`useMultiLineFetch`).
 */
export const useMultipleTextValues = (
  query: QuerySpec | null | undefined,
  durationMs: number = DURATION_GAUGE_WIDGET_MS,
  /** When false (off-screen widget), ticks skip fetching; re-entry
   *  refetches once via the visibility bridge. */
  inViewport = true,
): UseMultipleTextValuesResult => {
  const [entries, setEntries] = useState<MultipleTextValuesEntryValue[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasNoData, setHasNoData] = useState(false);

  const bindings = (query?.bindings ?? []).map((binding) => ({
    source: binding.source ?? query?.source ?? "",
    address: binding.address,
  }));

  // Stable effect dep: `bindings` is rebuilt on every render, so the effect
  // keys off a serialised form of the (machine, node) pairs and reads the
  // live array through a ref. Same shape as `useLastWidgetValue`.
  const bindingsKey = bindings
    .map((binding) => `${binding.source}:${binding.address}`)
    .join("|");
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;

  const latestLoadRef = useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  useEffect(() => {
    setEntries([]);
    setHasNoData(false);
    setIsLoading(false);
    if (!bindingsKey) {
      latestLoadRef.current = null;
      return;
    }

    let cancelled = false;
    // Overlap guard: a slow endpoint must never stack parallel fetches when
    // the interval fires again mid-request.
    let inFlight = false;
    // Only the first load shows the spinner; later ticks replace values in
    // place so the table never flickers.
    let isInitialLoad = true;

    const fetchEntry = async (
      source: string,
      address: string,
    ): Promise<MultipleTextValuesEntryValue> => {
      if (!source || !address) return NO_ENTRY;
      try {
        const to = new Date().toISOString();
        const from = new Date(Date.now() - durationMs).toISOString();
        const result = await getQueryTimeseriesForNodeUseCase({
          group: source,
          source: address,
          frequency: "raw",
          from,
          to,
        });
        if (!result.success) return NO_ENTRY;
        const values = (result.data as { values?: QueryValue[] } | undefined)
          ?.values;
        const last = values?.[values.length - 1];
        if (!last) return NO_ENTRY;
        const raw = last.value;
        // Strings pass through so a status-style node renders as text.
        // Anything else (boolean / nested) drops to null — same rule the
        // single-value hook applies.
        const accepted =
          typeof raw === "number" || typeof raw === "string" ? raw : null;
        return accepted === null
          ? NO_ENTRY
          : { value: accepted, hasNoData: false };
      } catch {
        return NO_ENTRY;
      }
    };

    const load = async () => {
      if (inFlight || document.hidden || !inViewportRef.current) return;
      inFlight = true;
      if (isInitialLoad) {
        isInitialLoad = false;
        setIsLoading(true);
      }
      try {
        const results = await Promise.all(
          bindingsRef.current.map((binding) =>
            fetchEntry(binding.source, binding.address),
          ),
        );
        if (cancelled) return;
        setEntries(results);
        setHasNoData(results.length > 0 && results.every((r) => r.hasNoData));
      } catch {
        if (!cancelled) setEntries([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          inFlight = false;
        }
      }
    };

    latestLoadRef.current = load;
    void load();
    // Always raw — the user-picked polling interval drives refresh.
    const pollingMs = resolvePollingMs(
      query?.sampling?.frequency,
      query?.sampling?.polling_interval,
    );
    const intervalId =
      pollingMs != null ? setInterval(() => void load(), pollingMs) : undefined;
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
      latestLoadRef.current = null;
      setIsLoading(false);
    };
  }, [
    bindingsKey,
    durationMs,
    query?.sampling?.frequency,
    query?.sampling?.polling_interval,
  ]);

  return { entries, isLoading, hasNoData };
};
